import React, { useState, useRef, useEffect } from "react";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import { showSuccess, showError } from "toast/toast";
import { useNavigate } from "react-router-dom";
import { Agent, BrokerageFirm, Office } from "../../types"; // Adjust the import path as necessary
import { addressLine1Validation, cityValidation, emailValidation, firstNameValidation, lastNameValidation, passwordValidation, phone2Validation, phoneValidation, stateValidation, zipcodeValidation } from "validationSchemas/common";
import { ag } from "assets/libs/chart.js/chunks/helpers.segment";
import { getAuthCookie } from "../../utils/authCookies";

const apiKey: string = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

interface AgentDetails {
  brokerageFirmAffiliation: string;
  managingBroker: string;
  agentID: string;
  licenseNumber: string;
  licenseExpiryDate: string;
};

interface FirmInfo {
  companyID: string;
  companyName: string;
  address: Address;
  specialities: string;
  regionsServed: string;
}

interface Address {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  zip: string;
}

// interface Agent {
//   id: string;
//   email: string;
//   password: string;
//   userType: string;
//   firstName: string;
//   lastName: string;
//   phone1: string;
//   phone2: string;
//   firmDetails: FirmInfo;
//   agentDetails: AgentDetails;
//   status: string;
//   licenseType: string;
//   created_at: string;  
// }

const SignageBrokerageEditAgent = () => {
  const { firmId } = useParams<{ firmId: string }>();
  const [brokerageFirm, setBrokerageFirm] = useState<BrokerageFirm | null>(null);
  const { agentId } = useParams<{ agentId: string }>();
  const [agent, setAgent] = useState<Agent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [formSubmitting, setFormSubmitting] = useState(false);
  const token = getAuthCookie("sessionToken");
  const navigate = useNavigate();
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries: ["places"],
  });

  const [address, setAddress] = useState("");
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [offices, setOffices] = useState<Office[]>([]);

  useEffect(() => {
    const fetchAgentData = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/users/${agentId}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const data = await response.json();
        if (data) {
          setBrokerageFirm(data?.agentDetails?.brokerageFirmAffiliation)
          setAgent(data);
        } else {
          console.error("Incomplete data", data);
        }
      } catch (error) {
        console.error("Error fetching agent data:", error);
        setError("Failed to fetch agent data");
      } finally {
        setLoading(false);
      }
    };
    fetchAgentData();
  }, [agentId, token]);

  useEffect(() => {
    const fetchOffices = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/offices-search?created_by.id=${firmId}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const data = await response.json();
        setOffices(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchOffices();
  }, [token]);

  // useEffect(() => {
  //   if (agent) {
  //     formik.setValues({
  //       firstName: agent.firstName || "",
  //       lastName: agent.lastName || "",
  //       phone1: agent.phone1 || "",
  //       phone2: agent.phone2 || "",
  //       email: agent.email || "",
  //       password: "",
  //       brokerageFirmAffiliation: agent.agentDetails.brokerageFirmAffiliation || "",
  //       agentID: agent.agentDetails.agentID || "",
  //       licenseNumber: agent.agentDetails.licenseNumber || "",
  //       licenseExpiryDate: agent.agentDetails.licenseExpiryDate || "",
  //       addressLine1: agent.firmDetails.address.addressLine1 || "",
  //       addressLine2: agent.firmDetails.address.addressLine2 || "",
  //       city: agent.firmDetails.address.city || "",
  //       state: agent.firmDetails.address.state || "",
  //       zipcode: agent.firmDetails.address.zip || "",
  //       statuss: agent.status || "",
  //       cityCode: agent.firmDetails.address.cityCode || "",
  //       stateCode: agent.firmDetails.address.stateCode || "",
  //       countryCode: agent.firmDetails.address.countryCode || "",
  //       latitude: agent?.firmDetails?.address?.latitude || 0,
  //       longitude: agent.firmDetails.address.longitude || 0,
  //     });
  //   }
  // }, [agent]);

  function getOfficeById(officeId: string) {
    return offices?.find(office => office?._id === officeId) || null;
  }
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: agent?.firstName || "",
      lastName: agent?.lastName || "",
      phone1: agent?.phone1 || "",
      phone2: agent?.phone2 || "",
      email: agent?.email || "",
      password: "",
      brokerageFirmAffiliation: agent?.agentDetails?.brokerageFirmAffiliation || "",
      agentID: agent?.agentDetails?.agentID || "",
      licenseNumber: agent?.agentDetails?.licenseNumber || "",
      licenseExpiryDate: agent?.agentDetails?.licenseExpiryDate || "",
      addressLine1: agent?.firmDetails?.address?.addressLine1 || "",
      addressLine2: agent?.firmDetails?.address?.addressLine2 || "",
      city: agent?.firmDetails?.address?.city || "",
      state: agent?.firmDetails?.address?.state || "",
      zipcode: agent?.firmDetails?.address?.zip || "",
      statuss: agent?.status || "",
      officeId: agent?.offices?.[0]?._id || "", // Assuming the first office is selected by default
      cityCode: agent?.firmDetails?.address?.cityCode || "",
      stateCode: agent?.firmDetails?.address?.stateCode || "",
      countryCode: agent?.firmDetails?.address?.countryCode || "",
      latitude: agent?.firmDetails?.address?.latitude || 0,
      longitude: agent?.firmDetails?.address?.longitude || 0,
    },
    validationSchema: Yup.object({
      firstName: firstNameValidation,
      lastName: lastNameValidation,
      phone1: phoneValidation,
      phone2: phone2Validation,
      email: emailValidation,
      // password: passwordValidation,
      addressLine1: addressLine1Validation,
      city: cityValidation,
      state: stateValidation,
      zipcode: zipcodeValidation,
    }),
    onSubmit: async (values) => {
      console.log("Submitting form...", values);
      setFormSubmitting(true);

      const selectedOfficeId = values.officeId;
      const selectedOffice = getOfficeById(values?.officeId);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users/${agentId}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              email: values.email,
              password: values.password,
              userType: "agent",
              firstName: values.firstName,
              lastName: values.lastName,
              phone1: values.phone1,
              phone2: values.phone2,
              licenseType: "BROKERAGE_AGENT",
              status: values.statuss,
              firmDetails: {
                companyID: "",
                companyName: "",
                companyWebsite: "",
                address: {
                  addressLine1: values.addressLine1,
                  addressLine2: values.addressLine2,
                  city: values.city,
                  state: values.state,
                  country: "US",
                  zip: values.zipcode,
                  phone: values.phone1,
                  cityCode: values.cityCode,
                  stateCode: values.stateCode,
                  countryCode: values.countryCode,
                  latitude: values.latitude,
                  longitude: values.longitude,
                },
              },
              agentDetails: {
                brokerageFirmAffiliation:values.brokerageFirmAffiliation,
                brokerageFirmID: brokerageFirm?.id,
                agentID: values.agentID,
                licenseNumber: values.licenseNumber,
                licenseExpiryDate: values.licenseExpiryDate,
              },
              offices: [{
                _id: selectedOffice?._id,
                officeName: selectedOffice?.officeName,
                officeType: selectedOffice?.officeType,
                officeAddress: {
                  addressLine1: selectedOffice?.officeAddress?.[0]?.addressLine1,
                  city: selectedOffice?.officeAddress?.[0]?.city,
                  state: selectedOffice?.officeAddress?.[0]?.state,
                  country: selectedOffice?.officeAddress?.[0]?.country,
                  zip: selectedOffice?.officeAddress?.[0]?.zip,
                  addressLine2: selectedOffice?.officeAddress?.[0]?.addressLine2 || "",
                  cityCode: selectedOffice?.officeAddress?.[0]?.cityCode || "",
                  stateCode: selectedOffice?.officeAddress?.[0]?.stateCode || "",
                  countryCode: selectedOffice?.officeAddress?.[0]?.countryCode || "",
                  latitude: selectedOffice?.officeAddress?.[0]?.latitude || 0,
                  longitude: selectedOffice?.officeAddress?.[0]?.longitude || 0,
                }
              }],
            }),
          }
        );

        const result: any = await response.json();
        if (!response.ok || result?.error) {
          const message = result?.error || result?.message || "Failed to save data";
          console.error("API Error:", message);
          showError(message);
          throw new Error(message);
        }
        showSuccess("Agent updated successfully!");
        navigate(`/signage/brokerageFirm/${firmId}#agents`);
      } catch (err) {
        console.error(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setFormSubmitting(false);
      }
    },
  });

  const handlePlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (!place || !place.address_components) {
        showError("Please select a valid address from the suggestions.");
        return;
      }
      const addressComponents = place.address_components;
      if (addressComponents) {
        const getComponent = (type: string) => {
          const component = addressComponents?.find(c => c.types.includes(type));
          return component ? { long_name: component.long_name, short_name: component.short_name } : { long_name: '', short_name: '' };
        };

        const streetNumber = getComponent('street_number').long_name;
        const route = getComponent('route').long_name;

        const cityObj = getComponent('locality') || getComponent('administrative_area_level_2');
        const stateObj = getComponent('administrative_area_level_1');
        const postalCode = getComponent('postal_code').long_name;
        const countryObj = getComponent('country');


        const street = `${streetNumber} ${route}`.trim();

        const latitude = place.geometry?.location?.lat();
        const longitude = place.geometry?.location?.lng();
        formik.setFieldValue('addressLine1', street);
        formik.setFieldValue('city', cityObj.long_name);
        formik.setFieldValue('cityCode', cityObj.short_name); // <-- Add this
        formik.setFieldValue('state', stateObj.long_name);
        formik.setFieldValue('stateCode', stateObj.short_name); // <-- Add this
        formik.setFieldValue('zipcode', postalCode);
        formik.setFieldValue('country', countryObj.long_name);
        formik.setFieldValue('countryCode', countryObj.short_name);

        if (latitude && longitude) {
          formik.setFieldValue('latitude', latitude);
          formik.setFieldValue('longitude', longitude);
        }
        // :white_tick: ZIP code logic with fallback
        if (postalCode) {
          formik.setFieldValue("zipcode", postalCode);
        } else if (latitude && longitude) {
          // Fallback to reverse geocoding
          fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
          )
            .then((res) => res.json())
            .then((data) => {
              const postalComp = data.results[0]?.address_components.find((c: any) =>
                c.types.includes("postal_code")
              );
              if (postalComp) {
                console.log("Postal Code from Reverse Geocoding:", postalComp.long_name);
                formik.setFieldValue("zipcode", postalComp.long_name);
              } else {
                console.warn("No postal_code found even from reverse geocoding");
              }
            })
            .catch((err) => console.error("Reverse geocoding error:", err));
        }
      }
    }
  };

  const clearGoogleAddressFields = () => {
    formik.setFieldValue('city', '', false);
    formik.setFieldValue('cityCode', '', false);
    formik.setFieldValue('state', '', false);
    formik.setFieldValue('stateCode', '', false);
    formik.setFieldValue('zipcode', '', false);
    formik.setFieldValue('country', '', false);
    formik.setFieldValue('countryCode', '', false);
    formik.setFieldValue('latitude', 0, false);
    formik.setFieldValue('longitude', 0, false);
  };

  const handleAddressLine1Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(e);
    if (!e.target.value.trim()) {
      clearGoogleAddressFields();
    }
  };

  if (!isLoaded) return <div>Loading...</div>;

  return (
    <div className="content">
      <div className="page-container mt-3 col-md-9">
        <h3 className="m-0 text-center fw-bold addheaders">Edit Brokerage Agent</h3>
      </div>
      {/* <h2 className="d-flex justify-content-center">Add Office </h2> */}
      <div className="row ms-0">
        <div className="col-md-12" id="row-overlap">
          <div className="col-md-9">
            <div className="addheaders-content mt-1 card-bg-fill">
              <div className="p-2 px-4 mt-1">
                <form onSubmit={formik.handleSubmit}>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        First Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="firstName"
                        name="firstName"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.firstName}
                        required
                      />
                      {formik.touched.firstName &&
                        formik.errors.firstName ? (
                        <div className="text-danger">
                          {formik.errors.firstName}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Last Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="lastName"
                        name="lastName"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.lastName}
                        required
                      />
                      {formik.touched.lastName && formik.errors.lastName ? (
                        <div className="text-danger">
                          {formik.errors.lastName}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Phone <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="phone1"
                        name="phone1"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.phone1}
                        required
                      />
                      {formik.touched.phone1 && formik.errors.phone1 ? (
                        <div className="text-danger">
                          {formik.errors.phone1}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Secondary Phone Number{" "}
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="phone2"
                        name="phone2"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.phone2}
                      />
                      {formik.touched.phone2 && formik.errors.phone2 ? (
                        <div className="text-danger">
                          {formik.errors.phone2}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Email <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        id="email"
                        name="email"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.email}
                        readOnly
                      />
                      {formik.touched.email && formik.errors.email ? (
                        <div className="text-danger">
                          {formik.errors.email}
                        </div>
                      ) : null}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Password
                      </label>
                      <div className="position-relative auth-pass-inputgroup">
                        <input
                          type="password"
                          className="form-control pe-5 password-input"
                          id="password"
                          name="password"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.password}
                          aria-describedby="password"
                          pattern="(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}"

                        />
                        <button
                          className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted password-addon material-shadow-none"
                          type="button"
                          id="password-addon"
                        >
                          <i className="ri-eye-fill align-middle"></i>
                        </button>
                        {formik.touched.password &&
                          formik.errors.password ? (
                          <div className="text-danger">
                            {formik.errors.password}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <hr />
                  <h4>Professional Details </h4>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Brokerage Firm Affiliation{" "}
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="brokerageFirmAffiliation"
                        name="brokerageFirmAffiliation"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.brokerageFirmAffiliation}
                        aria-describedby="brokerageFirmAffiliation"
                        placeholder="Enter Brokerage Firm Affiliation"
                      />
                      {formik.touched.brokerageFirmAffiliation && formik.errors.brokerageFirmAffiliation ? (
                        <div className="text-danger">
                          {formik.errors.brokerageFirmAffiliation}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Office</label>
                      <select
                        className="form-control br-8"
                        id="officeId"
                        name="officeId"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.officeId}
                      >
                        <option value="">- Select -</option>
                        {offices !== null &&
                          offices?.map((office) => (
                            <option value={office._id}>
                              {office.officeName}
                            </option>
                          ))}
                      </select>
                      {formik.touched.officeId && formik.errors.officeId ? (
                        <div className="text-danger">
                          {formik.errors.officeId}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Agent ID/Code </label>
                      <input
                        type="text"
                        className="form-control"
                        id="agentID"
                        name="agentID"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.agentID}
                        aria-describedby="agentID"
                        placeholder="Enter Agent ID/Code"
                      />
                      {formik.touched.agentID && formik.errors.agentID ? (
                        <div className="text-danger">
                          {formik.errors.agentID}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">License Number </label>
                      <input
                        type="text"
                        className="form-control"
                        id="licenseNumber"
                        name="licenseNumber"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.licenseNumber}
                        aria-describedby="licenseNumber"
                        placeholder="Enter license number"
                      />
                      {formik.touched.licenseNumber && formik.errors.licenseNumber ? (
                        <div className="text-danger">
                          {formik.errors.licenseNumber}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">License Expiry Date </label>
                      <input
                        type="date"
                        className="form-control"
                        id="licenseExpiryDate"
                        name="licenseExpiryDate"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.licenseExpiryDate}
                        aria-describedby="licenseExpiryDate"
                        placeholder="Enter license expiry date"
                      />
                      {formik.touched.licenseExpiryDate && formik.errors.licenseExpiryDate ? (
                        <div className="text-danger">
                          {formik.errors.licenseExpiryDate}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Street Name <span className="text-danger">*</span>
                      </label>
                      {
                        isLoaded &&
                        <Autocomplete
                          onLoad={(autocomplete) =>
                            (autocompleteRef.current = autocomplete)
                          }
                          onPlaceChanged={handlePlaceChanged}
                        >
                          <input
                            type="text"
                            className="form-control"
                            id="addressLine1"
                            name="addressLine1"
                            onChange={handleAddressLine1Change}
                            onBlur={formik.handleBlur}
                            value={formik.values.addressLine1}
                            required
                          />
                        </Autocomplete>
                      }
                      {formik.touched.addressLine1 && formik.errors.addressLine1 ? (
                        <div className="text-danger">
                          {formik.errors.addressLine1}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Address line 2 </label>
                      <input
                        type="text"
                        className="form-control"
                        id="addressLine2"
                        name="addressLine2"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.addressLine2}
                      />
                      {formik.touched.addressLine2 && formik.errors.addressLine2 ? (
                        <div className="text-danger">
                          {formik.errors.addressLine2}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">City</label>
                      <input
                        type="text"
                        className="form-control"
                        id="city"
                        name="city"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.city}
                        disabled
                      />
                      {formik.touched.city && formik.errors.city ? (
                        <div className="text-danger">
                          {formik.errors.city}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">State</label>
                      <input
                        type="text"
                        className="form-control"
                        id="state"
                        name="state"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.state}
                        disabled
                      />
                      {formik.touched.state && formik.errors.state ? (
                        <div className="text-danger">
                          {formik.errors.state}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Zip code</label>
                      <input
                        type="text"
                        className="form-control"
                        id="zipcode"
                        name="zipcode"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.zipcode}
                        disabled
                      />
                      {formik.touched.zipcode && formik.errors.zipcode ? (
                        <div className="text-danger">
                          {formik.errors.zipcode}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-2 mb-3">
                      <label className="form-label">
                        Status <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-control"
                        id="statuss"
                        name="statuss"
                        required
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.statuss}
                      >
                        <option value="">- Select -</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                      {formik.touched.statuss && formik.errors.statuss ? (
                        <div className="text-danger">
                          {formik.errors.statuss}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="row mb-3 rcust-margin-4">
                    <div className="col-md-12 d-flex justify-content-between">
                      <button
                        className="btn btn-outline-secondary br-8 backbutton"
                        type="button"
                        onClick={() => navigate(-1)}
                      >
                        Back
                      </button>

                      <button className="btn btn-primary submit-btn" type="submit">
                        Save
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignageBrokerageEditAgent;
